/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { SearchFacet } from '@/plp/data/models/api';
import { Product } from '@/plp/data/models/product';
import { PriceRange } from '@adobe-commerce/elsie/components';

const BOOLEAN_NO = 'no';
const BOOLEAN_YES = 'yes';

const getRangeFacetFilter = (facet: SearchFacet) => {
  const selectedRange = facet.buckets.find((bucket) => bucket.selected);
  if (!selectedRange) {
    return null;
  }
  return {
    attribute: facet.attribute,
    range: {
      from: selectedRange.from,
      to: selectedRange.to,
    },
  };
};

const getScalarFacetFilter = (facet: SearchFacet) => {
  const scalarSelections = facet.buckets.reduce((accu, curr) => {
    if (curr.selected) {
      accu.push(curr.title);
    }
    return accu;
  }, [] as string[]);
  if (scalarSelections.length === 0) {
    return null;
  }
  return {
    attribute: facet.attribute,
    in: scalarSelections,
  };
};

const getFilterSelection = (facet: SearchFacet) => {
  switch (facet.buckets[0]?.__typename) {
    case 'RangeBucket':
      return getRangeFacetFilter(facet);
    case 'ScalarBucket':
    case 'CategoryView':
      return getScalarFacetFilter(facet);
    default:
      break;
  }
};

const getCurrencyType = (items: Product[]) => {
  const firstItem = items[0];
  if (firstItem?.price?.regular?.amount?.currency) {
    return firstItem.price.regular.amount.currency;
  } else if (firstItem?.priceRange?.minimum?.regular?.amount?.currency) {
    return firstItem?.priceRange?.minimum?.regular?.amount?.currency;
  }
  return 'USD'; // Default currency if not found
};

const getRangeBucketLabel = (bucket: any, facet: SearchFacet, currency: string | null) => {
  if (facet?.attribute === 'price') {
    //Temp fix for Price range not handling value when minumum is zero
    if (bucket.from === 0) {
      return (
        <div style={'display:flex'}>
          <PriceRange amount={bucket.from} currency={currency || 'USD'} />
          <span style={'padding: 0 2px'}>{'-'}</span>
          <PriceRange amount={bucket.to} currency={currency || 'USD'} />
        </div>
      );
    }
    return (
      <PriceRange minimumAmount={bucket.from} maximumAmount={bucket.to} currency={currency || 'USD'} display="dash" />
    );
  }
  return bucket.title;
};

const getScalarBucketLabel = (bucket: any, facet: SearchFacet) => {
  if (bucket.title === BOOLEAN_NO) {
    return `Not ${facet.title}`;
  } else if (bucket.title === BOOLEAN_YES) {
    return facet.title;
  }
  return bucket.title;
};

const getBucketLabel = (bucket: any, facet: SearchFacet, currency: string | null) => {
  switch (bucket.__typename) {
    case 'RangeBucket':
      return getRangeBucketLabel(bucket, facet, currency);
    case 'ScalarBucket':
    case 'CategoryView':
      return getScalarBucketLabel(bucket, facet);
    default:
      break;
  }
};

export { getBucketLabel, getCurrencyType, getFilterSelection };
